import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../../../utils/auth';
import { ArrowLeft, MapPin, Calendar, Clock, Users, DollarSign, Package, FileText, Activity, Edit2, Plus, Truck } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import JobStatusBadge from '../../../components/production/JobStatusBadge';
import PriorityBadge from '../../../components/production/PriorityBadge';
import VisitStatusBadge from '../../../components/production/VisitStatusBadge';
import PlanVisitModal from '../../../components/production/PlanVisitModal';
import '../../../styles/admin/production/JobDetail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [visits, setVisits] = useState([]);
  const [teams, setTeams] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [team, setTeam] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPlanVisitModal, setShowPlanVisitModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const [jobRes, visitsRes, activityRes, teamsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs/${jobId}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/job-visits/job/${jobId}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/jobs/${jobId}/activity`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/vehicles`, { headers: getAuthHeader() })
      ]);

      setJob(jobRes.data);
      setVisits(visitsRes.data);
      setActivityLogs(activityRes.data.activity || []);
      setTeams(teamsRes.data);
      setVehicles(vehiclesRes.data);

      // Fetch team if assigned
      if (jobRes.data.assignment.team_id) {
        const teamRes = await axios.get(
          `${API_URL}/api/teams/${jobRes.data.assignment.team_id}`,
          { headers: getAuthHeader() }
        );
        setTeam(teamRes.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching job details:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;

    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API_URL}/api/jobs/${jobId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );

      await fetchJobDetails();
      setShowStatusModal(false);
      setNewStatus('');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const statusOptions = [
    { value: 'to_schedule', label: 'À planifier' },
    { value: 'scheduled', label: 'Planifié' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'completed', label: 'Terminé' },
    { value: 'paused', label: 'En pause' },
    { value: 'cancelled', label: 'Annulé' }
  ];

  if (loading) {
    return <div className="job-detail-loading">Chargement...</div>;
  }

  if (!job) {
    return <div className="job-detail-error">Job introuvable</div>;
  }

  return (
    <div className="job-detail-container">
      <div className="job-detail-header">
        <button onClick={() => navigate('/admin/production/jobs')} className="btn-back">
          <ArrowLeft size={20} />
          Retour à la liste
        </button>
        <div className="header-title">
          <h2>{job.job_number}</h2>
          <div className="header-badges">
            <JobStatusBadge status={job.status} />
            <PriorityBadge priority={job.priority} />
          </div>
        </div>
        <button
          onClick={() => {
            setNewStatus(job.status);
            setShowStatusModal(true);
          }}
          className="btn-change-status"
        >
          <Edit2 size={16} />
          Changer statut
        </button>
      </div>

      <div className="job-detail-content">
        {/* Left Column */}
        <div className="detail-left">
          {/* General Info */}
          <div className="detail-card">
            <h3>Informations générales</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Titre</label>
                <p>{job.title}</p>
              </div>
              {job.description && (
                <div className="info-item full-width">
                  <label>Description</label>
                  <p>{job.description}</p>
                </div>
              )}
              <div className="info-item">
                <label>Source</label>
                <p>{job.source === 'quote_acceptance' ? 'Devis accepté' : job.source}</p>
              </div>
              <div className="info-item">
                <label>Type client</label>
                <p>{job.client_type === 'residential' ? 'Résidentiel' : 'Commercial'}</p>
              </div>
            </div>
          </div>

          {/* Service Address */}
          <div className="detail-card">
            <h3>
              <MapPin size={20} />
              Adresse d'intervention
            </h3>
            <div className="address-content">
              <p className="address-line">{job.service_address.street}</p>
              <p className="address-line">
                {job.service_address.city}, {job.service_address.province} {job.service_address.postal_code}
              </p>
            </div>
          </div>

          {/* Services */}
          <div className="detail-card">
            <h3>
              <Package size={20} />
              Services inclus ({job.services.length})
            </h3>
            <div className="services-list">
              {job.services.map((service, idx) => (
                <div key={idx} className="service-item">
                  <div className="service-header">
                    <span className="service-name">{service.name}</span>
                    <span className="service-price">{service.price.toFixed(2)} $</span>
                  </div>
                  <div className="service-meta">
                    <span className="service-code">{service.code}</span>
                    <span className="service-duration">{service.estimated_duration_minutes} min</span>
                    <span className="service-crew">{service.crew_size_required} personne(s)</span>
                  </div>
                  {service.notes && (
                    <p className="service-notes">{service.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Visites planifiées */}
          <div className="detail-card">
            <div className="card-header-with-action">
              <h3>
                <Calendar size={20} />
                Visites planifiées ({visits.length})
              </h3>
              <button
                onClick={() => setShowPlanVisitModal(true)}
                className="btn-add-visit"
              >
                <Plus size={16} />
                Planifier une visite
              </button>
            </div>
            
            {visits.length === 0 ? (
              <div className="empty-visits">
                <Calendar size={48} />
                <p>Aucune visite planifiée</p>
                <small>Cliquez sur "Planifier une visite" pour commencer</small>
              </div>
            ) : (
              <div className="visits-list">
                {visits.map((visit) => {
                  const visitTeam = teams.find(t => t.id === visit.team_id);
                  const visitVehicle = vehicles.find(v => v.id === visit.vehicle_id);
                  
                  return (
                    <div key={visit.id} className="visit-card">
                      <div className="visit-header">
                        <div className="visit-number">Visite #{visit.visit_number}</div>
                        <VisitStatusBadge status={visit.status} />
                      </div>
                      <div className="visit-body">
                        <div className="visit-info-row">
                          <Calendar size={14} />
                          <span>
                            {visit.scheduled_start ? 
                              format(new Date(visit.scheduled_start), 'dd MMMM yyyy à HH:mm', { locale: fr }) :
                              'Date non définie'
                            }
                          </span>
                        </div>
                        <div className="visit-info-row">
                          <Clock size={14} />
                          <span>{visit.estimated_duration_minutes} minutes</span>
                        </div>
                        {visitTeam && (
                          <div className="visit-info-row">
                            <Users size={14} />
                            <span>{visitTeam.name}</span>
                          </div>
                        )}
                        {visitVehicle && (
                          <div className="visit-info-row">
                            <Truck size={14} />
                            <span>{visitVehicle.name}</span>
                          </div>
                        )}
                        {visit.notes_before && (
                          <div className="visit-notes">
                            <small>{visit.notes_before}</small>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notes */}
          {(job.internal_notes || job.customer_notes) && (
            <div className="detail-card">
              <h3>
                <FileText size={20} />
                Notes
              </h3>
              {job.internal_notes && (
                <div className="note-section">
                  <label>Notes internes:</label>
                  <p>{job.internal_notes}</p>
                </div>
              )}
              {job.customer_notes && (
                <div className="note-section">
                  <label>Notes client:</label>
                  <p>{job.customer_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="detail-right">
          {/* Assignment */}
          <div className="detail-card">
            <h3>
              <Users size={20} />
              Assignation
            </h3>
            {job.assignment.team_id ? (
              <div className="assignment-content">
                <div className="info-item">
                  <label>Équipe</label>
                  <p>{team ? team.name : job.assignment.team_id}</p>
                </div>
                {job.assignment.scheduled_start && (
                  <>
                    <div className="info-item">
                      <label>Début prévu</label>
                      <p>
                        <Calendar size={14} />
                        {format(new Date(job.assignment.scheduled_start), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                      </p>
                    </div>
                    {job.assignment.scheduled_end && (
                      <div className="info-item">
                        <label>Fin prévue</label>
                        <p>
                          <Clock size={14} />
                          {format(new Date(job.assignment.scheduled_end), 'HH:mm', { locale: fr })}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <p className="no-assignment">Aucune équipe assignée</p>
            )}
          </div>

          {/* Scheduling */}
          <div className="detail-card">
            <h3>
              <Calendar size={20} />
              Préférences de planification
            </h3>
            <div className="scheduling-content">
              {job.scheduling.target_start_date && (
                <div className="info-item">
                  <label>Date cible</label>
                  <p>{format(new Date(job.scheduling.target_start_date), 'dd MMMM yyyy', { locale: fr })}</p>
                </div>
              )}
              <div className="info-item">
                <label>Flexibilité</label>
                <p>{job.scheduling.is_flexible ? 'Flexible' : 'Non flexible'}</p>
              </div>
              {job.scheduling.season && (
                <div className="info-item">
                  <label>Saison</label>
                  <p>{job.scheduling.season}</p>
                </div>
              )}
            </div>
          </div>

          {/* Estimations */}
          <div className="detail-card">
            <h3>
              <DollarSign size={20} />
              Estimations
            </h3>
            <div className="estimations-grid">
              <div className="estimation-item">
                <label>Durée</label>
                <p>{job.estimated.duration_minutes} min</p>
              </div>
              <div className="estimation-item">
                <label>Déplacement</label>
                <p>{job.estimated.drive_time_minutes} min</p>
              </div>
              <div className="estimation-item">
                <label>Revenus</label>
                <p className="amount-positive">{job.estimated.revenue.toFixed(2)} $</p>
              </div>
              <div className="estimation-item">
                <label>Coûts main d'oeuvre</label>
                <p className="amount-negative">{job.estimated.labor_cost.toFixed(2)} $</p>
              </div>
              <div className="estimation-item">
                <label>Coûts matériaux</label>
                <p className="amount-negative">{job.estimated.material_cost.toFixed(2)} $</p>
              </div>
              <div className="estimation-item highlight">
                <label>Marge brute</label>
                <p className="amount-positive">{job.estimated.gross_margin.toFixed(2)} $</p>
              </div>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="detail-card">
            <h3>
              <Activity size={20} />
              Historique ({activityLogs.length})
            </h3>
            <div className="activity-timeline">
              {activityLogs.length === 0 ? (
                <p className="no-activity">Aucune activité</p>
              ) : (
                activityLogs.map((log, idx) => (
                  <div key={idx} className="activity-item">
                    <div className="activity-dot"></div>
                    <div className="activity-content">
                      <p className="activity-action">{getActivityLabel(log.action)}</p>
                      <p className="activity-meta">
                        Par {log.performed_by.name} - {format(new Date(log.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Changer le statut</h3>
            <div className="modal-body">
              <div className="form-group">
                <label>Nouveau statut</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="form-control"
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={handleStatusChange}
                disabled={updatingStatus || newStatus === job.status}
                className="btn-primary"
              >
                {updatingStatus ? 'Mise à jour...' : 'Confirmer'}
              </button>
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn-secondary"
                disabled={updatingStatus}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Visit Modal */}
      {showPlanVisitModal && (
        <PlanVisitModal
          jobId={jobId}
          onClose={() => setShowPlanVisitModal(false)}
          onVisitCreated={fetchJobDetails}
        />
      )}
    </div>
  );
};

const getActivityLabel = (action) => {
  const labels = {
    created: 'Job créée',
    updated: 'Job mise à jour',
    assigned: 'Job assignée',
    status_changed: 'Statut modifié',
    deleted: 'Job supprimée'
  };
  return labels[action] || action;
};

export default JobDetail;